import React, { useState, useEffect } from 'react';
import { 
    Card, 
    Button, 
    Modal, 
    Form, 
    Input, 
    Select, 
    DatePicker, 
    InputNumber, 
    Table, 
    Tag, 
    Space, 
    message, 
    Divider,
    Row,
    Col,
    Typography,
    Alert,
    Timeline,
    Badge
} from 'antd';
import { 
    CreditCardOutlined, 
    CalendarOutlined, 
    DollarOutlined, 
    LinkOutlined,
    SendOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const QuotePaymentManager = ({ quote, onPaymentSuccess }) => {
    const { user } = useAuth();
    const [paymentLinks, setPaymentLinks] = useState([]);
    const [paymentSchedule, setPaymentSchedule] = useState(null);
    const [loading, setLoading] = useState(false);
    const [createLinkModalVisible, setCreateLinkModalVisible] = useState(false);
    const [createScheduleModalVisible, setCreateScheduleModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [scheduleForm] = Form.useForm();

    useEffect(() => {
        if (quote?.id) {
            fetchPaymentData();
        }
    }, [quote?.id]);

    const fetchPaymentData = async () => {
        try {
            setLoading(true);
            const [linksResponse, scheduleResponse] = await Promise.all([
                api.getQuotePaymentLinks(quote.id),
                api.getQuotePaymentSchedule(quote.id)
            ]);

            if (linksResponse.success) {
                setPaymentLinks(linksResponse.data.payment_links || []);
            }

            if (scheduleResponse.success) {
                setPaymentSchedule(scheduleResponse.data.payment_schedule);
            }
        } catch (error) {
            console.error('Failed to fetch payment data:', error);
            message.error('Failed to load payment information');
        } finally {
            setLoading(false);
        }
    };

    const handleCreatePaymentLink = async (values) => {
        try {
            setLoading(true);
            const response = await api.createQuotePaymentLink(quote.id, {
                ...values,
                expires_at: values.expires_at?.toISOString()
            });

            if (response.success) {
                message.success('Payment link created successfully');
                setCreateLinkModalVisible(false);
                form.resetFields();
                fetchPaymentData();
            } else {
                message.error(response.message || 'Failed to create payment link');
            }
        } catch (error) {
            console.error('Failed to create payment link:', error);
            message.error('Failed to create payment link');
        } finally {
            setLoading(false);
        }
    };

    const handleCreatePaymentSchedule = async (values) => {
        try {
            setLoading(true);
            const response = await api.createQuotePaymentSchedule(quote.id, {
                ...values,
                milestones: values.milestones.map(milestone => ({
                    ...milestone,
                    due_date: milestone.due_date.toISOString()
                }))
            });

            if (response.success) {
                message.success('Payment schedule created successfully');
                setCreateScheduleModalVisible(false);
                scheduleForm.resetFields();
                fetchPaymentData();
            } else {
                message.error(response.message || 'Failed to create payment schedule');
            }
        } catch (error) {
            console.error('Failed to create payment schedule:', error);
            message.error('Failed to create payment schedule');
        } finally {
            setLoading(false);
        }
    };

    const copyPaymentLink = (link) => {
        navigator.clipboard.writeText(link);
        message.success('Payment link copied to clipboard');
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: 'orange',
            completed: 'green',
            failed: 'red',
            expired: 'gray'
        };
        return colors[status] || 'default';
    };

    const getPaymentTypeIcon = (type) => {
        const icons = {
            advance: <DollarOutlined />,
            full: <CreditCardOutlined />,
            installment: <CalendarOutlined />
        };
        return icons[type] || <CreditCardOutlined />;
    };

    const paymentLinkColumns = [
        {
            title: 'Type',
            dataIndex: 'payment_type',
            key: 'payment_type',
            render: (type) => (
                <Space>
                    {getPaymentTypeIcon(type)}
                    <Text strong>{type.charAt(0).toUpperCase() + type.slice(1)} Payment</Text>
                </Space>
            )
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: (amount) => <Text strong>₹{amount.toLocaleString()}</Text>
        },
        {
            title: 'Method',
            dataIndex: 'payment_method',
            key: 'payment_method',
            render: (method) => <Tag color="blue">{method.toUpperCase()}</Tag>
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={getStatusColor(status)}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                </Tag>
            )
        },
        {
            title: 'Expires',
            dataIndex: 'expires_at',
            key: 'expires_at',
            render: (expiresAt) => expiresAt ? dayjs(expiresAt).format('MMM DD, YYYY') : 'Never'
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button 
                        size="small" 
                        icon={<LinkOutlined />}
                        onClick={() => copyPaymentLink(record.payment_url)}
                    >
                        Copy Link
                    </Button>
                    <Button 
                        size="small" 
                        icon={<SendOutlined />}
                        onClick={() => sendPaymentLink(record)}
                    >
                        Send
                    </Button>
                </Space>
            )
        }
    ];

    const sendPaymentLink = (paymentLink) => {
        // This would integrate with email/SMS service
        message.info('Payment link sending functionality would be implemented here');
    };

    return (
        <div>
            <Row gutter={[16, 16]}>
                {/* Payment Links Section */}
                <Col span={24}>
                    <Card 
                        title={
                            <Space>
                                <LinkOutlined />
                                Payment Links
                            </Space>
                        }
                        extra={
                            user?.user_type === 'vendor' ? (
                                <Button 
                                    type="primary" 
                                    icon={<LinkOutlined />}
                                    onClick={() => setCreateLinkModalVisible(true)}
                                >
                                    Create Payment Link
                                </Button>
                            ) : null
                        }
                    >
                        <Table
                            columns={paymentLinkColumns}
                            dataSource={paymentLinks}
                            rowKey="id"
                            pagination={false}
                            loading={loading}
                            locale={{ emptyText: 'No payment links created yet' }}
                        />
                    </Card>
                </Col>

                {/* Payment Schedule Section */}
                <Col span={24}>
                    <Card 
                        title={
                            <Space>
                                <CalendarOutlined />
                                Payment Schedule
                            </Space>
                        }
                        extra={
                            <Button 
                                type="primary" 
                                icon={<CalendarOutlined />}
                                onClick={() => setCreateScheduleModalVisible(true)}
                            >
                                Create Schedule
                            </Button>
                        }
                    >
                        {paymentSchedule ? (
                            <div>
                                <Row gutter={[16, 16]}>
                                    <Col span={12}>
                                        <Card size="small">
                                            <Text strong>Advance Amount</Text>
                                            <div style={{ fontSize: '18px', color: '#1890ff' }}>
                                                ₹{paymentSchedule.advance_amount?.toLocaleString()}
                                            </div>
                                        </Card>
                                    </Col>
                                    <Col span={12}>
                                        <Card size="small">
                                            <Text strong>Status</Text>
                                            <div>
                                                <Tag color={getStatusColor(paymentSchedule.status)}>
                                                    {paymentSchedule.status?.toUpperCase()}
                                                </Tag>
                                            </div>
                                        </Card>
                                    </Col>
                                </Row>
                                
                                <Divider />
                                
                                <Title level={5}>Payment Milestones</Title>
                                <Timeline>
                                    {paymentSchedule.milestones?.map((milestone, index) => (
                                        <Timeline.Item
                                            key={index}
                                            dot={<Badge count={index + 1} style={{ backgroundColor: '#1890ff' }} />}
                                        >
                                            <div>
                                                <Text strong>{milestone.name}</Text>
                                                <div>Amount: ₹{milestone.amount?.toLocaleString()}</div>
                                                <div>Due: {dayjs(milestone.due_date).format('MMM DD, YYYY')}</div>
                                                {milestone.description && (
                                                    <div style={{ color: '#666' }}>{milestone.description}</div>
                                                )}
                                            </div>
                                        </Timeline.Item>
                                    ))}
                                </Timeline>
                            </div>
                        ) : (
                            <Alert
                                message="No payment schedule created"
                                description="Create a payment schedule to set up milestone-based payments for this quote."
                                type="info"
                                showIcon
                            />
                        )}
                    </Card>
                </Col>
            </Row>

            {/* Create Payment Link Modal */}
            <Modal
                title="Create Payment Link"
                open={createLinkModalVisible}
                onCancel={() => setCreateLinkModalVisible(false)}
                footer={null}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleCreatePaymentLink}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="payment_type"
                                label="Payment Type"
                                rules={[{ required: true, message: 'Please select payment type' }]}
                            >
                                <Select placeholder="Select payment type">
                                    <Option value="advance">Advance Payment</Option>
                                    <Option value="full">Full Payment</Option>
                                    <Option value="installment">Installment</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="amount"
                                label="Amount (₹)"
                                rules={[{ required: true, message: 'Please enter amount' }]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={1}
                                    placeholder="Enter amount"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="payment_method"
                                label="Payment Method"
                                rules={[{ required: true, message: 'Please select payment method' }]}
                            >
                                <Select placeholder="Select payment method">
                                    <Option value="razorpay">Razorpay</Option>
                                    <Option value="stripe">Stripe</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="expires_at"
                                label="Expires At"
                            >
                                <DatePicker 
                                    style={{ width: '100%' }}
                                    showTime
                                    format="YYYY-MM-DD HH:mm"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="description"
                        label="Description"
                    >
                        <TextArea 
                            rows={3}
                            placeholder="Enter payment description (optional)"
                        />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={loading}>
                                Create Payment Link
                            </Button>
                            <Button onClick={() => setCreateLinkModalVisible(false)}>
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Create Payment Schedule Modal */}
            <Modal
                title="Create Payment Schedule"
                open={createScheduleModalVisible}
                onCancel={() => setCreateScheduleModalVisible(false)}
                footer={null}
                width={800}
            >
                <Form
                    form={scheduleForm}
                    layout="vertical"
                    onFinish={handleCreatePaymentSchedule}
                >
                    <Form.Item
                        name="advance_amount"
                        label="Advance Amount (₹)"
                        rules={[{ required: true, message: 'Please enter advance amount' }]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            min={1}
                            placeholder="Enter advance amount"
                        />
                    </Form.Item>

                    <Form.List name="milestones">
                        {(fields, { add, remove }) => (
                            <>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                    <Title level={5}>Payment Milestones</Title>
                                    <Button type="dashed" onClick={() => add()} icon={<CalendarOutlined />}>
                                        Add Milestone
                                    </Button>
                                </div>

                                {fields.map(({ key, name, ...restField }) => (
                                    <Card key={key} size="small" style={{ marginBottom: 16 }}>
                                        <Row gutter={16}>
                                            <Col span={8}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'name']}
                                                    label="Milestone Name"
                                                    rules={[{ required: true, message: 'Please enter milestone name' }]}
                                                >
                                                    <Input placeholder="e.g., 50% Payment" />
                                                </Form.Item>
                                            </Col>
                                            <Col span={6}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'amount']}
                                                    label="Amount (₹)"
                                                    rules={[{ required: true, message: 'Please enter amount' }]}
                                                >
                                                    <InputNumber
                                                        style={{ width: '100%' }}
                                                        min={1}
                                                        placeholder="Amount"
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col span={8}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'due_date']}
                                                    label="Due Date"
                                                    rules={[{ required: true, message: 'Please select due date' }]}
                                                >
                                                    <DatePicker 
                                                        style={{ width: '100%' }}
                                                        format="YYYY-MM-DD"
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col span={2}>
                                                <Form.Item label=" ">
                                                    <Button 
                                                        type="text" 
                                                        danger 
                                                        onClick={() => remove(name)}
                                                        icon={<ExclamationCircleOutlined />}
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'description']}
                                            label="Description"
                                        >
                                            <TextArea 
                                                rows={2}
                                                placeholder="Optional description"
                                            />
                                        </Form.Item>
                                    </Card>
                                ))}
                            </>
                        )}
                    </Form.List>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={loading}>
                                Create Payment Schedule
                            </Button>
                            <Button onClick={() => setCreateScheduleModalVisible(false)}>
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default QuotePaymentManager;
